// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'
import { toAdminListParams } from '@/core/lib/table-query'

// External Module Imports
import { allCategoriesQueryOptions } from '@/modules/category/category.hooks'
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'

// Module Imports
import {
  questionDetailQueryOptions,
  questionListQueryOptions,
} from '@/modules/question/question.hooks'
import { questionSearchSchema } from '@/modules/question/question.schemas'
import { questionPoolOptionsQueryOptions } from '@/modules/question-pool/question-pool.hooks'

export const questionRoute = createRoute({
  path: '/questions',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const questionListRoute = createRoute({
  getParentRoute: () => questionRoute,
  path: '/',
  validateSearch: questionSearchSchema,
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'questions.list',
      'Soru listesine erişim iznininiz yok.'
    )
  },
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    await Promise.all([
      queryClient.ensureQueryData(
        questionListQueryOptions(toAdminListParams(deps, 'question'))
      ),
      queryClient.ensureQueryData(allCategoriesQueryOptions()),
      queryClient.ensureQueryData(questionPoolOptionsQueryOptions()),
    ])
  },
  head: () => ({
    meta: [
      {
        title: 'Soru Listesi',
      },
    ],
  }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/question/pages/list-question.page')
  ),
})

export const questionDetailRoute = createRoute({
  getParentRoute: () => questionRoute,
  path: '$id',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'questions.detail',
      'Soru detayına erişim izniniz yok.'
    )
  },
  head: async ({ params: { id } }) => {
    const question = await queryClient.fetchQuery(
      questionDetailQueryOptions(id)
    )

    return {
      meta: [
        {
          title: `Soru | ${question.question}`,
        },
      ],
    }
  },
  loader: async ({ params: { id } }) => {
    await queryClient.ensureQueryData(questionDetailQueryOptions(id))
  },
  component: lazyRouteComponent(
    () => import('@/modules/question/pages/question-detail.page')
  ),
})

export const questionCreateRoute = createRoute({
  getParentRoute: () => questionRoute,
  path: '/create',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'questions.create',
      'Soru oluşturmak için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Soru Oluştur' }],
  }),
  loader: async () => {
    await Promise.all([
      queryClient.ensureQueryData(allCategoriesQueryOptions()),
      queryClient.ensureQueryData(questionPoolOptionsQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/question/pages/create-question.page')
  ),
})

export const questionEditRoute = createRoute({
  getParentRoute: () => questionRoute,
  path: '/$id/edit',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'questions.edit',
      'Soru düzenlemek için yetkiniz yok.'
    )
  },
  head: async ({ params: { id } }) => {
    const question = await queryClient.fetchQuery(
      questionDetailQueryOptions(id)
    )

    return {
      meta: [
        {
          title: `Soru Düzenle ${question.question}`,
        },
      ],
    }
  },
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(questionDetailQueryOptions(id)),
      queryClient.ensureQueryData(allCategoriesQueryOptions()),
      queryClient.ensureQueryData(questionPoolOptionsQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/question/pages/edit-question.page')
  ),
})
